// Menu Panel component for the chat application

import React, { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Drawer,
  Divider,
  Menu,
  MenuItem,
  Box,
  Typography,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  useMediaQuery,
  IconButton,
  Button,
  Collapse,
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import LogoutIcon from '@mui/icons-material/Logout';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

import { useMessageHandler } from '../contexts/MessageContext.js';
import { useChatSession } from '../contexts/ChatSessionContext.js';
import CustomSettings from './CustomSettings';
import ProjectsList from './ProjectsList';
import ChatSessionItem from './ChatSessionItem';
import MainSessionList from './MainSessionList';
import MenuHeader from './MenuHeader';
import GroupHeader from './GroupHeader';

// Styled components for custom appearance
const CustomDrawer = styled(Drawer)(({ theme }) => ({
  width: '100%',
  height: '100%',
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    backgroundColor: theme.palette.mode === 'dark'
      ? theme.palette.grey[900]
      : theme.palette.grey[200],
    color: theme.palette.text.primary,
    boxSizing: 'border-box',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflowX: 'hidden',
    position: 'relative',
    boxShadow: theme.palette.mode === 'dark'
      ? '0px 0px 10px rgba(0, 0, 0, 0.5)'
      : '0px 0px 10px rgba(0, 0, 0, 0.1)',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    },
    [theme.breakpoints.up('sm')]: {
      width: '100%',
      position: 'relative'
    }
  },
}));

// Style for user account section
const UserAccountSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

// Section container for grouping elements
const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
}));

const ScrollableChatList = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  flexBasis: 0,
  marginBottom: theme.spacing(1),
  overflowY: 'auto',
  overflowX: 'hidden',
  flex: '1 1 auto',
  minHeight: 0,
  // Add custom scrollbar styling
  '&::-webkit-scrollbar': {
    width: '6px'
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.2)'
      : 'rgba(0, 0, 0, 0.2)',
    borderRadius: '3px'
  },
  '&::-webkit-scrollbar-thumb:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.3)'
      : 'rgba(0, 0, 0, 0.3)'
  }
}));

// Main MenuPanel component
const MenuPanel = ({
  open,
  onClose,
  onLogoutClick,
  userEmail,
  onEngineChange
}) => {

  const {
    fetchChatSessions,
    newChatSession,
    groupedChatSessions,
    groupName,
    projects = [],
    moveSessionToProject,
  } = useChatSession();

  const { setMessages, isProcessing, isPlaybackActive, isTranscribing } = useMessageHandler();
  const { currentSession, setCurrentSession } = useMessageHandler();
  const [loadingSessionArea, setLoadingSessionArea] = useState(false);
  const [hoveredSession, setHoveredSession] = useState(null);
  const { t } = useTranslation();
  const [showSettings, setShowSettings] = useState(false);
  const [chatsVisible, setChatsVisible] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Fetch chat sessions when authenticated
  useEffect(() => {
    try {
      setLoadingSessionArea(true);
      fetchChatSessions();
    } catch (error) {
      console.error('Error loading chat sessions: ', error);
    } finally {
      setLoadingSessionArea(false);
    }
  }, [fetchChatSessions]);

  const handleNewChat = async () => {
    const msg = await newChatSession();
    await setMessages(msg);
    setCurrentSession('');
    await fetchChatSessions();

    // Close menu panel on mobile after creating new chat
    if (isMobile) {
      onClose();
    }
  };

  const UserAccount = ({ userEmail, onLogoutClick }) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const { t } = useTranslation();

    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const handleLogout = () => {
      handleClose();
      onLogoutClick();
    };

    return (
      <>
        <UserAccountSection onClick={handleClick}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon fontSize="small" color="action" />
            <Typography variant="body2" sx={{
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {userEmail}
            </Typography>
          </Box>
          <ExpandMoreIcon fontSize="small" color="action" />
        </UserAccountSection>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <MenuItem onClick={handleLogout}>
            <ListItemIcon>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={t('auth.logout')} />
          </MenuItem>
        </Menu>
      </>
    );
  };

  // Check if a session is in any project
  const isSessionInProject = (sessionId) => {
    // Check if projects is defined and is an array
    if (!projects || !Array.isArray(projects)) {
      return false;
    }
    return projects.some(project =>
      project && project.sessions && Array.isArray(project.sessions) &&
      project.sessions.includes(sessionId)
    );
  };

  return (
    <CustomDrawer variant="persistent" anchor="right" open={open}>
      {/* Header Section */}
      <MenuHeader
        onSettingsClick={() => setShowSettings(true)}
        onClose={onClose}
        data-testid="settings-button"
      />

      {/* User account section */}
      <Section>
        <UserAccount
          userEmail={userEmail}
          onLogoutClick={onLogoutClick}
        />
      </Section>
      <Divider />

      {/* Projects List */}
      <Section>
        <ProjectsList onClose={onClose} onEngineChange={onEngineChange} />
      </Section>
      <Divider />

      {/* Chat Sessions Header with Expand/Collapse and New Chat buttons */}
      <Box sx={{ p: 1 }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 0
        }}>
          {/* Main expand/collapse button for chat sessions */}
          <Button
            variant="outlined"
            onClick={() => setChatsVisible(!chatsVisible)} // You'll need to add this state
            startIcon={chatsVisible ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{
              textTransform: 'none',
              justifyContent: 'flex-start',
              flexGrow: 1,
              mr: 1,
              pl: 1,
              borderColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)',
              backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              '&:hover': {
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              }
            }}
          >
            {t('menu.chatSessions')}
          </Button>

          {/* Small "+" button for creating new chat */}
          <IconButton
            size="small"
            onClick={handleNewChat}
            disabled={isProcessing || isPlaybackActive || isTranscribing}
            sx={{
              borderRadius: '4px',
              border: theme => `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.12)'}`,
              backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              padding: '4px',
              width: '32px',
              height: '32px',
              '&:hover': {
                backgroundColor: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              }
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Scrollable Chat List */}
      <Collapse
        in={chatsVisible}
        timeout="auto"
        unmountOnExit          /* keep your Collapse behaviour */
        sx={{
          /* the root itself */
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          flexBasis: 0,

          /* make both internal wrappers part of the flex chain */
          '& .MuiCollapse-wrapper': {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            flexBasis: 0,
          },
          '& .MuiCollapse-wrapperInner': {
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            flexBasis: 0,
          },
        }}
      >
        <ScrollableChatList>
          <MainSessionList onMoveSession={moveSessionToProject} theme={theme}>
            {Object.keys(groupedChatSessions).map((group) => {
              // Filter out sessions that are in projects
              const sessionsInGroup = Array.isArray(groupedChatSessions[group])
                ? groupedChatSessions[group]
                : [];

              // Filter out sessions that are in projects
              const filteredSessions = sessionsInGroup.filter(
                session => !isSessionInProject(session.sessionId)
              );

              if (filteredSessions.length === 0) return null;

              return (
                <div key={group}>
                  <GroupHeader title={groupName(group)} />
                  {filteredSessions.map((session) => (
                    <div
                      key={session.sessionId}
                      onMouseEnter={() => setHoveredSession(session.sessionId)}
                      onMouseLeave={() => setHoveredSession(null)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <ChatSessionItem
                        session={session}
                        isHovered={hoveredSession === session.sessionId}
                        isCurrentSession={currentSession === session.sessionId}
                        isInProject={false}
                        onClose={onClose}
                        onEngineChange={onEngineChange}
                      />
                    </div>
                  ))}
                  <Divider sx={{ my: 0.5 }} />
                </div>
              );
            })}

            {loadingSessionArea && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </MainSessionList>
        </ScrollableChatList>
      </Collapse>

      {/* Settings Dialog */}
      <CustomSettings
        open={showSettings}
        onClose={() => setShowSettings(false)}
      />
    </CustomDrawer >
  );
};

export default MenuPanel;